import { Typography, TypographyProps } from '@mui/material'
import { GridColDef } from '@mui/x-data-grid-premium'
import { DateFormatterOptions, useFormatter } from '@omnea-app/fe-i18n/useFormatter'
import { removeTimezoneFromDate } from '@omnea-app/packages/universal/date-utils/createDateWithoutTimezone'
import { parseDateString } from '@omnea-app/packages/universal/date-utils/parseDateString'
import { isDefined } from '@omnea-app/packages/universal/type-guards/isDefined'
import { DateEditCell } from '../../data-grid/DateEditCell'
import { BaseColumnConfig } from '../types'
import { baseColumnConfig } from './baseColumnConfig'
import { getDateFilterOperators } from './getDateFilterOperators'
import type { GridValidRowModel } from '@mui/x-data-grid/models/gridRows'

export type DateColumnConfig<T extends GridValidRowModel> = BaseColumnConfig<T, 'date'> & {
  columnType: 'date'
  editable?: boolean
  filterable?: boolean
  formatDateOptions?: Partial<DateFormatterOptions>
  textVariant?: TypographyProps['variant']
  disableTimezone?: boolean
}

const DateCell = ({
  value,
  formatOptions,
  textVariant,
  disableTimezone,
}: {
  value: Date | null
  formatOptions?: Partial<DateFormatterOptions>
  textVariant?: TypographyProps['variant']
  disableTimezone?: boolean
}) => {
  const { formatDate } = useFormatter()

  if (!isDefined(value)) return null

  const date = disableTimezone ? removeTimezoneFromDate(value) : new Date(value)

  return <Typography variant={textVariant}>{formatDate(date, { format: 'long', ...formatOptions })}</Typography>
}

export const getDateColumn = <T extends GridValidRowModel>({
  columnType,
  style,
  editable,
  ...config
}: DateColumnConfig<T>): GridColDef<T> => ({
  ...baseColumnConfig,
  ...style,
  ...config,
  type: 'date',
  filterOperators: getDateFilterOperators(),
  groupable: false,
  valueGetter: config.valueGetter ? config.valueGetter : (_, row) => parseDateString(row[config.field]),
  renderCell: (params) => (
    <DateCell
      {...(config?.getDataTestId ? { ['data-testid']: config.getDataTestId(params) } : {})}
      value={params.value}
      formatOptions={config.formatDateOptions}
      textVariant={config.textVariant}
      disableTimezone={config.disableTimezone}
    />
  ),
  ...(editable && {
    editable: true,
    renderEditCell: (params) => <DateEditCell {...params} />,
    valueSetter: (value, row) => ({ ...row, [config.field]: toCalendarDay(value) }),
  }),
})

// Dates arrive as UTC midnight, so reading the day in local time would give the day before west of UTC.
const toCalendarDay = (value: unknown): string | null => {
  const date = parseDateString(value)
  if (!isDefined(date)) return null
  return date.toISOString().slice(0, 10)
}
